import { getIdTokenJwt, getSessionUser } from "./auth/session";
import { isAuthLost, recoverSession, sessionLost } from "./auth/authRecovery";
import {
  STEP_UP_HEADER,
  handleStepUpError,
  heldProof,
  isFinishingRoute,
  isSigningRoute,
  stepUpCode,
} from "./auth/stepUp";
import Axios, { AxiosHeaders, InternalAxiosRequestConfig } from "axios";
import { setupCache, buildWebStorage } from "axios-cache-interceptor";
import { createAxiosCacheHooks } from "axios-cache-hooks";
import { enqueueSnackbar } from "notistack";
import { HELPCENTER_API_KEY, getHelpCenterBaseUrl } from './config/helpcenter';
import { getDataViewUrl, getApiUrl, getInstallName } from './config/product';
import { setDbWarming } from "./app/DbWakeContext";
import { trackRequestStart, trackRequestEnd } from "./app/InflightRequests";
import versionInfo from "./version.json";

let __inflightCounter = 0;
const nextInflightId = () => `req-${++__inflightCounter}`;

// Neon cold-start can take 8-15s on a fully scale-to-zero stack. Two retries
// at the backend's suggested 3s gives up after ~6s, well inside cold-start time.
// Five retries with a progressive ceiling covers a realistic worst case.
const DB_WARMING_MAX_RETRIES = 5;
const DB_WARMING_MAX_WAIT_MS = 6000;

// DB_TRANSIENT — backend emitted a 503 for a non-wake connection issue
// (e.g. Cloud Run cold-start pool pressure, or Supabase pool exhaustion on CRM).
// Retry silently with a tight budget; do NOT flash the banner — the DB itself
// isn't waking up, so claiming "Reconnecting to AeroNet" would be misleading
// past the first retry.
const DB_TRANSIENT_MAX_RETRIES = 2;
const DB_TRANSIENT_MAX_WAIT_MS = 2000;

// COLD_START — Cloud Run scale-to-zero returns a body-less 503 from the
// Knative/LB layer BEFORE the FastAPI app boots, so neither DB_WARMING nor
// DB_TRANSIENT can apply (those codes come from the app). Bites CRM hardest
// because aeronet-fast-api-crm is lower-traffic than SCM and scales to zero
// more often per user session. Silent exponential backoff.
//
// The ladder these two constants produce, since it is easy to misread and has
// misled two sessions into quoting "~7s" (2026-08-29): the wait is
// min(1000 * 2^(n-1), COLD_START_MAX_WAIT_MS), so the retries are
// 1s, 2s, 4s, then 5s x7 (the cap binds from the 4th on) = **~42s across 10
// retries** before the promise finally rejects. Keep this figure in step with
// the constants if you change either — a caller that renders an error state
// only sees it after the FULL ladder, so this number is the user-visible
// latency of every failure that enters this branch.
const COLD_START_MAX_RETRIES = 10;
const COLD_START_MAX_WAIT_MS = 5000;

const isTestEnvironment = process.env.NODE_ENV === "test";

Axios.defaults.baseURL = isTestEnvironment ? undefined : getApiUrl();
const awsRegex = new RegExp(/^.*\.amazonaws.com\/.*/);

// App version for versioned doc lookups (docs served under v/<version>/ else
// global/). Sourced from version.json (generated from package.json at build).
export const getAppVersion = (): string => {
  const v = (versionInfo as { version?: string }).version;
  return typeof v === "string" && /^\d+\.\d+\.\d+$/.test(v.trim()) ? v.trim() : "global";
};

/**
 * Returns the base URL of the stationery render service, always trailing-slashed.
 * Driven by REACT_APP_STATIONERY_URL — set in .env.production / .env.local.
 * Centralized here so the 18 preview components (sales orders, quotes, invoices,
 * etc.) all hit the same endpoint, and so the JWT interceptor below can attach
 * the Cognito ID token automatically.
 */
export const getStationeryURL = (): string => {
  if (process.env.REACT_APP_STATIONERY_URL) {
    const url = process.env.REACT_APP_STATIONERY_URL;
    return url.endsWith("/") ? url : `${url}/`;
  }
  // One render service for every tenant, .dev and .app alike. There is no
  // stationery.aeronet.app — it has no DNS record (measured 2026-09-28), so a
  // .app fallback to it could only ever fail.
  return "https://stationery.aeronet.dev/";
};

export const axios = setupCache(Axios, {
  storage: buildWebStorage(localStorage, "aeronet-api-cache:"),
  cacheTakeover: false,
});
export const { useQuery, useMutation } = createAxiosCacheHooks();

export const axiosRequestInterceptor = async (
  config: InternalAxiosRequestConfig
) => {
  // Tag every outgoing request so the BootProgress / debug surface can show
  // "Waiting on GET /v6/users/me — 12s". Done before the auth await so we
  // capture token-refresh time too.
  if (!(config as any).__inflightId) {
    const id = nextInflightId();
    (config as any).__inflightId = id;
    trackRequestStart(id, config.url || "", config.method || "GET");
  }

  // skip adding authorization if request is to aws directly
  if (config.url && awsRegex.test(config.url)) {
    return config;
  }

  // Cached session, refreshed if expired. Provider-agnostic: Cognito tenants
  // go through Amplify, GCIP tenants (aquipe) through the GCIP REST session.
  try {
    const token = await getIdTokenJwt();
    if (token) {
      setHeader(config, "Authorization", token);
    }
  } catch {
    // Unauthenticated request (e.g. login screen or session initializing)
  }
  // A sign-off inside the step-up window carries the proof already held, so a
  // batch asks for the password once (src/auth/stepUp.ts). Only the finishing
  // routes, and a Log Book entry's save (Aircraft Patch 5), ever see it.
  const proof = isFinishingRoute(config.url) || isSigningRoute(config.url, config.method) ? heldProof() : null;
  if (proof) {
    setHeader(config, STEP_UP_HEADER, proof);
  }
  return config;
};

// Response side of the in-flight tracker. Wired below alongside the error
// interceptor so success + failure + cancel paths all clear the entry.
const clearInflight = (configHolder: any) => {
  const id = configHolder?.config?.__inflightId ?? configHolder?.__inflightId;
  if (id) trackRequestEnd(id);
};

let axiosJiraUserHeaders: { email?: string; username?: string } = {};

export const setAxiosJiraUserHeaders = (currentUser?: { [key: string]: any }) => {
  axiosJiraUserHeaders = {
    email: currentUser?.email,
    username: currentUser?.username,
  };
};

const axiosJiraRequestInterceptor = async (
  config: InternalAxiosRequestConfig
) => {
  config = setHeader(config, "X-API-Key", HELPCENTER_API_KEY);
  config = setHeader(config, "X-Install-Name", getInstallName());

  if (axiosJiraUserHeaders.email) {
    config = setHeader(config, "X-User-Email", axiosJiraUserHeaders.email);
  }

  if (axiosJiraUserHeaders.username) {
    config = setHeader(config, "X-User-Name", axiosJiraUserHeaders.username);
  }

  try {
    const currentUser = await getSessionUser();
    const email = currentUser?.attributes?.email;
    const username = currentUser?.username || currentUser?.attributes?.name;

    if (!axiosJiraUserHeaders.email && email) {
      config = setHeader(config, "X-User-Email", email);
    }

    if (!axiosJiraUserHeaders.username && username) {
      config = setHeader(config, "X-User-Name", username);
    }
  } catch {
    // Helpcenter auth is header/API-key based; unauthenticated lookups still get install headers.
  }

  return config;
};

// Apply the auth interceptor on the cached axios explicitly.
// Previously this was wired only via index.tsx on the bare `axios` import.
// In practice — likely due to axios-cache-interceptor wrapping the instance —
// the interceptor chain on the cached `axios` didn't pick it up for the new
// stationery cross-origin calls.  Wiring here removes the ambiguity.
axios.interceptors.request.use(axiosRequestInterceptor);

// A request on the main API client is one whose 401 means "the session is
// gone" (the 401 branch of axiosResponseErrorInterceptor). Marked even when
// there is no token to attach: a boot request sent without one is exactly the
// request that comes back 401. Only this client: axiosXero shares
// axiosRequestInterceptor, and a Xero 401 is about the Xero grant, never the
// Aeronet session (useXeroPush.tsx).
axios.interceptors.request.use((config) => {
  if (!(config.url && awsRegex.test(config.url))) {
    (config as any).__aeronetAuth = true;
  }
  return config;
});

// Dedicated axios instance for the stationery render service (v2).
// Separate from the cached `axios` because axios-cache-interceptor's wrapping
// somehow stops the auth interceptor from firing on cross-origin POSTs.
// Use as: axiosStationery.post("render", payload)  // baseURL = getStationeryURL()
//
// The base URL is pinned, never defaulted. `Axios.create()` copies
// `Axios.defaults`, and `Axios.defaults.baseURL` is the TENANT API (set above),
// so a bare create() plus an `if (!config.baseURL)` fallback never fell back:
// every "render" went to <tenant api>/render and 404'd on every tenant, both
// auth planes (H101 — peak + g2 Cloud Run logs, 2026-09-28).
export const axiosStationery = Axios.create({ baseURL: getStationeryURL() });

// v2 requires the ID token with the explicit "Bearer " scheme.
// token_use must be "id" — getIdTokenJwt(), never the access token. That helper
// dispatches on the tenant's auth plane (Cognito via Amplify, GCIP via its REST
// session), and the render service verifies either issuer.
export const axiosStationeryRequestInterceptor = async (
  config: InternalAxiosRequestConfig
) => {
  config.baseURL = getStationeryURL();
  try {
    const token = await getIdTokenJwt();
    if (token) {
      const headerVal = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      setHeader(config, "Authorization", headerVal);
    }
  } catch {
    // Unauthenticated
  }
  return config;
};
axiosStationery.interceptors.request.use(axiosStationeryRequestInterceptor);

// Axios instance for Jira/Helpcenter API


// Ticket portal no longer uses axiosJira — it uses the JWT main axios at /v6/ticket-portal.
export const axiosJira = Axios.create({
  baseURL: isTestEnvironment ? undefined : getHelpCenterBaseUrl(),
});

axiosJira.interceptors.request.use(axiosJiraRequestInterceptor);

/**
 * Xero integration service (per-user OAuth). Routes are mounted at the base:
 * GET /connect, GET /state, POST /disconnect, POST /{entity}/push.
 * Base e.g. https://aeronet-fast-api-g2-bjtmq2eija-ts.a.run.app/v6/xero.
 * In test environment: undefined (so MSW matches relative paths).
 * Auth: ID token (same interceptor as the main instance).
 */
export const getXeroApiUrl = (): string | undefined => {
  if (isTestEnvironment) return undefined;
  if (process.env.REACT_APP_XERO_API_URL) {
    return process.env.REACT_APP_XERO_API_URL.replace(/\/+$/, "");
  }
  const apiBase = getApiUrl();
  return apiBase ? `${apiBase.replace(/\/+$/, "")}/v6/xero` : "/v6/xero";
};

export const axiosXero = Axios.create({
  baseURL: getXeroApiUrl(),
});
// Axios.create() inherits defaults.cache from setupCache(Axios) above — clear it,
// nothing on this instance is cached (/state must always be read fresh).
delete (axiosXero.defaults as { cache?: unknown }).cache;

if (!isTestEnvironment) {
  axiosXero.interceptors.request.use(axiosRequestInterceptor);
}

/**
 * Data Views service — module-scoped read-only datasets.
 * Auth: Cognito IdToken.
 *
 * Prefer `services/dataViews` (queryDataset / listDatasets) — it sends std
 * { contract_version, format, parameters } and unwraps the response envelope.
 *
 * Paths (module = job_costs):
 *   GET  /job_costs/datasets
 *   POST /job_costs/datasets/{wip_value|wip_movement|gross_profit|stock_sales}/query
 */
export const getDataViewURL = (): string | undefined => {
  if (isTestEnvironment) return undefined;
  return getDataViewUrl();
};

export const axiosDataView = Axios.create({
  baseURL: getDataViewURL(),
});
// Axios.create() inherits defaults.cache from setupCache(Axios) above — clear it.
delete (axiosDataView.defaults as { cache?: unknown }).cache;

const axiosDataViewRequestInterceptor = async (
  config: InternalAxiosRequestConfig
) => {
  if (!config.baseURL) {
    const base = getDataViewURL();
    if (base) config.baseURL = base;
  }
  try {
    const token = await getIdTokenJwt();
    if (token) {
      const headerVal = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      return setHeader(config, "Authorization", headerVal);
    }
  } catch {
    // Unauthenticated request
  }
  return config;
};
axiosDataView.interceptors.request.use(axiosDataViewRequestInterceptor);

export const getExportURL = (): string | undefined => {
  if (isTestEnvironment) return undefined;
  const url = process.env.REACT_APP_EXPORT_URL;
  if (!url) return undefined;
  return url.replace(/\/+$/, "");
};

export const axiosExport = Axios.create({
  baseURL: getExportURL(),
});
delete (axiosExport.defaults as { cache?: unknown }).cache;

// The base URL is pinned, never defaulted — same trap as axiosStationery above:
// `Axios.create({ baseURL: undefined })` inherits `Axios.defaults.baseURL`, the
// TENANT API, so an unset REACT_APP_EXPORT_URL used to post exports to
// <tenant api>/ instead of failing. Unset outside tests now fails loudly.
export const axiosExportRequestInterceptor = async (
  config: InternalAxiosRequestConfig
) => {
  const base = getExportURL();
  if (!isTestEnvironment && !base && !/^https?:\/\//i.test(config.url || "")) {
    throw new Error("Data export is not configured: REACT_APP_EXPORT_URL is unset");
  }
  config.baseURL = base;
  try {
    const token = await getIdTokenJwt();
    if (token) {
      const headerVal = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      return setHeader(config, "Authorization", headerVal);
    }
  } catch {
    // Unauthenticated request
  }
  return config;
};
axiosExport.interceptors.request.use(axiosExportRequestInterceptor);

function setHeader(
  config: InternalAxiosRequestConfig<any>,
  header: string,
  value: string
): InternalAxiosRequestConfig<any> {
  if (!config) {
    config = {
      headers: new AxiosHeaders(),
    };
  }
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }

  config.headers.set(header, value);
  return config;
}

/**
 * Whether a failed request reads as a Cloud Run / Traefik WAKE (the ladder
 * below retries it): no response, or a body-less 502 / 503 / 504, unless the
 * caller opted out (`noColdStartRetry`).
 *
 * 🔴 A CANCELLED request is never a wake (P2d D3). Its caller aborted it — a
 * grid whose filter, page or month moved on — so it has no response either,
 * and was retried as one: ten times over ~42 s, each retry rejecting at once
 * on the same aborted signal (so nothing reached the network), and then the
 * cancel was delivered ~42 s LATE. The Flight & Duty list mapped that late
 * cancel to an empty page and the shared grid applied it over the live rows:
 * "No results were found within the applied filter", with no request on the
 * wire. A cancel is the one no-response failure the browser CAN tell from a
 * boot (`isCancel`, `ERR_CANCELED`), so excluding it keeps the rule that a
 * CORS-less 500 and a real boot stay indistinguishable and both retried.
 */
export function coldStartRetryable(error: any, cfg: { noColdStartRetry?: boolean } = error?.config || {}): boolean {
  if (Axios.isCancel(error) || error?.code === "ERR_CANCELED") return false;
  return (
    (!error?.response ||
      error.response?.status === 502 ||
      error.response?.status === 503 ||
      error.response?.status === 504) &&
    !error?.response?.data?.code &&
    !cfg.noColdStartRetry
  );
}

export const axiosResponseErrorInterceptor = async (error) => {
  // Stop tracking this request as in-flight — both retry branches below and
  // the default error fallthrough need this cleared exactly once.
  clearInflight(error);

  // A sign-off the API wants a fresh password proof for (428), or refused the
  // proof of (403): ask, retry once, never sign out (src/auth/stepUp.ts).
  if (stepUpCode(error)) {
    return handleStepUpError(error, (retryCfg) =>
      Axios.request(retryCfg).catch((retryErr) => axiosResponseErrorInterceptor(retryErr))
    );
  }

  // Neon DB cold-start: backend returns 503 with { code: "DB_WARMING" }.
  // Flash the banner, wait the suggested delay, retry the original request, and suppress the snackbar.
  const cfg = error.config || {};

  // 401 on a request we authenticated: the session is expired or rejected,
  // not broken. Refresh it once and replay the request; if that cannot work,
  // go to the login screen (src/auth/authRecovery.ts). Ahead of every other
  // branch on purpose: `suppressErrorToast` callers need this too, and an auth
  // failure is never toasted. 401s from services that authenticate another
  // way (the help-centre API key) are not a session failure and fall through.
  if (error.response?.status === 401 && cfg.__aeronetAuth) {
    if (!cfg.__authRetried && !isAuthLost()) {
      cfg.__authRetried = true;
      if (await recoverSession()) {
        // The request interceptor runs again on the replay and attaches the
        // refreshed token. A failed replay has already been through this
        // interceptor chain, so it is passed on as it is.
        return Axios.request(cfg);
      }
    }
    sessionLost();
    return Promise.reject(error);
  }

  const isWarming =
    !isTestEnvironment &&
    error.response?.status === 503 &&
    error.response?.data?.code === "DB_WARMING";

  if (isWarming) {
    setDbWarming(true);
    cfg.__dbWarmRetries = (cfg.__dbWarmRetries ?? 0) + 1;
    if (cfg.__dbWarmRetries <= DB_WARMING_MAX_RETRIES) {
      const suggested = error.response.data.retry_after_ms ?? 3000;
      // Progressive backoff: each retry waits a little longer, capped.
      const wait = Math.min(suggested * cfg.__dbWarmRetries, DB_WARMING_MAX_WAIT_MS);
      await new Promise((r) => setTimeout(r, wait));
      try {
        const resp = await Axios.request(cfg);
        setDbWarming(false);
        return resp;
      } catch (retryErr: any) {
        if (
          retryErr.response?.status === 503 &&
          retryErr.response?.data?.code === "DB_WARMING"
        ) {
          // Still warming — recurse so the next retry uses the updated counter.
          return axiosResponseErrorInterceptor(retryErr);
        }
        setDbWarming(false);
        error = retryErr;
      }
    } else {
      setDbWarming(false);
    }
  }

  // DB_TRANSIENT: non-wake connection issue. Silent quick retry, no banner.
  const isTransient =
    !isTestEnvironment &&
    error.response?.status === 503 &&
    error.response?.data?.code === "DB_TRANSIENT";

  if (isTransient) {
    cfg.__dbTransientRetries = (cfg.__dbTransientRetries ?? 0) + 1;
    if (cfg.__dbTransientRetries <= DB_TRANSIENT_MAX_RETRIES) {
      const suggested = error.response.data.retry_after_ms ?? 1000;
      const wait = Math.min(suggested * cfg.__dbTransientRetries, DB_TRANSIENT_MAX_WAIT_MS);
      await new Promise((r) => setTimeout(r, wait));
      try {
        return await Axios.request(cfg);
      } catch (retryErr: any) {
        if (
          retryErr.response?.status === 503 &&
          retryErr.response?.data?.code === "DB_TRANSIENT"
        ) {
          return axiosResponseErrorInterceptor(retryErr);
        }
        error = retryErr;
      }
    }
  }

  // COLD_START: body-less 503 / 502 / Network Error from local Traefik or Cloud Run boot layer.
  // Silent retry with exponential backoff — 1s, 2s, 4s, then 5s x7 = ~42s over
  // 10 retries (see COLD_START_MAX_RETRIES/_WAIT_MS above; this comment used to
  // say "1s -> 2s -> 4s", which reads as a ~7s ladder and misled two sessions).
  // Covers first-paint API calls while waking.
  //
  // NOTE the predicate keys only on "no `code` in the body", so it cannot tell a
  // real boot from a PERMANENT error that happens to be a 503 — e.g.
  // /v6/calendar/* on a tenant with no calendar configured answers 503
  // {"detail":"Calendar not configured"} forever. Such a response is retried all
  // 10 times and its toast is fired ~42s late, on whatever page the user has
  // since navigated to (the snackbar is global and has no page affinity). If you
  // are adding a deterministic 5xx, give it a `code` like DB_WARMING/DB_TRANSIENT
  // so it exits here immediately.
  //
  // `noColdStartRetry` is the CALLER-SIDE half of that escape hatch, for the
  // case where the 5xx is permanent-by-design and the backend cannot be changed
  // to carry a `code` (e.g. a tenant with no calendar configured — g2's calendar
  // gap is deliberate, pending O365). Without it the ladder does not merely
  // waste ~42s: it keeps firing AFTER the route change, so one page's expected
  // 503 lands in the network log of whatever page the user has since navigated
  // to. On the 2026-08-30 g2 crawl that charged /app/calendar's two mount reads
  // against the next four unrelated modules (Documents, OFIs, Risk Register,
  // Metabase Reporting), all of which had in fact rendered normally, and each of
  // which was visited inside the 42s window. Retries that outlive their own page
  // are attributable to the wrong page by construction — so a caller that KNOWS
  // its 5xx is permanent must opt out rather than rely on the ladder expiring.
  const isColdStart = !isTestEnvironment && coldStartRetryable(error, cfg);

  if (isColdStart) {
    cfg.__coldStartRetries = (cfg.__coldStartRetries ?? 0) + 1;
    if (cfg.__coldStartRetries <= COLD_START_MAX_RETRIES) {
      const wait = Math.min(
        1000 * Math.pow(2, cfg.__coldStartRetries - 1),
        COLD_START_MAX_WAIT_MS
      );
      await new Promise((r) => setTimeout(r, wait));
      try {
        return await Axios.request(cfg);
      } catch (retryErr: any) {
        return axiosResponseErrorInterceptor(retryErr);
      }
    }
  }

  // Per-request opt-out: callers that handle an error inline (e.g. an expected
  // 404) set `suppressErrorToast` on the request config so the global snackbar
  // doesn't double-surface it. The error still rejects for the caller's catch.
  if (cfg.suppressErrorToast) {
    return Promise.reject(error);
  }

  if (!error.response) {
    // True network error / connection offline — or a request its caller
    // cancelled (P2d D3), which is neither and is not logged as one.
    if (!Axios.isCancel(error)) console.warn("[API Network Error]", error.config?.url || error.message);
    return Promise.reject(error);
  }

  /*
   * The request was made and the server responded with a
   * status code that falls out of the range of 2xx
   */
  // `handledErrorCodes`: a structured refusal (`detail: {code, …}`) the
  // caller turns into its own prompt — e.g. 409 `crew_unavailable` → "Assign
  // anyway" (components/useCrewUnavailableConfirm). Not toasted; it still
  // rejects for the caller.
  const handledCode = error.response.data?.detail?.code;
  if (handledCode && cfg.handledErrorCodes?.includes(handledCode)) {
    return Promise.reject(error);
  }

  // Attempt to display the error message from the api
  if (
    error.response.data &&
    Object.prototype.hasOwnProperty.call(error.response.data, "detail") &&
    error.response.data.detail
  ) {
    if (Array.isArray(error.response.data.detail)) {
      // `inlineValidationErrors`: the caller renders a 422's field list on the
      // form itself (utils/FormikErrors.ts serverFieldErrors), so toasting each
      // bare msg ("Required", "Required") here would only repeat it without the
      // field names. Every other failure still toasts.
      if (cfg.inlineValidationErrors && error.response.status === 422) {
        return Promise.reject(error);
      }
      for (let e of error.response.data.detail) {
        console.error(e.msg);
        enqueueSnackbar(e.msg, {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
      }
    } else {
      console.error(error.response.data.detail);
      enqueueSnackbar(error.response.data.detail, {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    return Promise.reject(error);
  }

  // If server responded with a non-2xx status code without a detail field,
  // do not show a generic "Other error" toast — let the caller handle it.
  if (error.response) {
    return Promise.reject(error);
  }

  if (!Axios.isCancel(error)) {
    const msg = typeof error?.message === "string" && error.message.length > 0
      ? error.message
      : null;
    if (msg) {
      console.error("Other error: ", msg);
      enqueueSnackbar(msg, {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    } else {
      console.error("Other error: (no message)", error);
    }
  }

  console.debug(error?.config);
  if (typeof error?.toJSON === "function") {
    try {
      console.debug(error.toJSON());
    } catch {
      // ignore — debug-only
    }
  }

  return Promise.reject(error);
};

// Apply response interceptor to every instance so DB_WARMING 503s are caught
// (and auto-retried) regardless of which client made the call.
//
// The former `axiosV5` instance is gone (2026-08-22). It existed only to serve
// `/v5/*` paths, which the API has not exposed since the v6 cutover — every one
// of its call sites 404'd. Its `baseURL: process.env.REACT_APP_API_URL_V5` was
// also dead: REACT_APP_* vars are not injected into the Cloud Run image, so CRA
// inlined `undefined`, and axios's mergeConfig then silently fell back to
// `Axios.defaults.baseURL` (the runtime `getApiUrl()` above). It worked by
// accident. Callers moved to `axios`; the only behavioural difference was
// caching, so the GETs that must stay live pass `{ cache: false }`.
export const axiosV5 = axios;
axios.interceptors.response.use(
  (response) => {
    clearInflight(response);
    return response;
  },
  axiosResponseErrorInterceptor
);
axiosStationery.interceptors.response.use(
  (response) => {
    clearInflight(response);
    return response;
  },
  axiosResponseErrorInterceptor
);
axiosJira.interceptors.response.use(
  (response) => {
    clearInflight(response);
    return response;
  },
  axiosResponseErrorInterceptor
);
axiosDataView.interceptors.response.use(
  (response) => {
    clearInflight(response);
    return response;
  },
  axiosResponseErrorInterceptor
);
axiosXero.interceptors.response.use(
  (response) => {
    clearInflight(response);
    return response;
  },
  axiosResponseErrorInterceptor
);
